<template>
  <b-container fluid>
    <group-componentized-module
      v-if="!!isSensorComponentized"
      :options="sensorInformation"
      :selected-information="selectedInformation"
      @on-change-selected-information="onChangeSelectedInformationHandler"
    />
    <general-and-parameters-layout
      :loading="loading"
      :requesting-data="requestingData"
      :variables="variables"
      :group-list="groupListHandler"
      :assetModuleId="selectedAssetModuleId"
      @create-group="createGroup"
      @apply-filter="applyFilter"
      @quit-renaming="quitRenaming"
      @get-group-grid="getGroupList"
      @reset-variable="resetVariable"
      @rename-group-request="renameGroupRequest"
      @delete-group-request="deleteGroupRequest"
      @moved-variable-by-drag="changeVariableGroup"
      @toggle-variable-visibility="toggleVisibility"
      @rename-variable-request="renameVariableRequest"
      @change-variable-group-by-button="changeVariableGroup"
      @restoreGroups="restoreGroups"
      @search-change="searchChange"
    />
  </b-container>
</template>
<script>
import eventBus from '@/services/globalEventBus';
import GeneralTabService
  from '@/domain/services/installation/supervision/sensors/generalAndParameters';
import GeneralAndParametersLayout
  from '@/domain/components/installation/supervision/sensors/GeneralAndParametersLayout';
import { alertModes } from '@/services/messages/alertService';
import { ModuleCategorys } from 'sigma-mappings';
import GroupComponentizedModule from '@/domain/components/installation/supervision/sensors/GroupComponentizedModule';
import event from '@/domain/events/engineeringModulesHeader';

export default {
  name: 'GeneralTab',
  props: {
    category: String,
    isSensorComponentized: {
      type: Boolean,
      default: false,
    },
    selectedInformation: {
      type: Object,
      default: () => ({}),
    },
    sensorInformation: {
      type: Array,
    },
  },
  components: {
    GroupComponentizedModule,
    GeneralAndParametersLayout,
  },
  data: () => ({
    loading: false,
    requestingData: false,
    showAll: false,
    supressLoading: false,
    groupList: [],
    variables: [],
    filter: [],
    selectedVariables: [],
  }),
  watch: {
    groupList() {
      this.variables = this.variableList;
    },
  },
  computed: {
    selectedAssetModuleId() {
      const { sensorId } = this.$route.params;
      const { assetModuleId } = this.selectedInformation || {};
      return this.category === ModuleCategorys.COMPONENTIZED
        ? assetModuleId
        : sensorId;
    },
    variableList() {
      return this.groupList
        .map(({ variables }) => variables)
        .reduce((item, index) => item.concat(index), []);
    },
    groupListHandler() {
      const filteredVariables = this.groupList.map(item => ({
        ...item,
        variables: item.variables.filter(({ id }) => this.filter.includes(id)),
      }));
      const filteredGroups = filteredVariables.filter(({ variables }) => variables.length);
      return this.filter.length ? filteredGroups : this.groupList;
    },
    groupWithoutReading() {
      return this.groupList.map(({ name, id }) => ({
        name,
        id,
      }));
    },
    dropdownItems() {
      return [
        {
          text: this.$t('actions.edit-group'),
          iconClass: 'fas fa-pencil-alt mr-2',
        },
        {
          text: this.$t('actions.delete-group'),
          iconClass: 'far fa-trash-alt mr-2',
          textColor: 'text-danger',
        },
      ];
    },
  },
  created() {
    this.getGroupList();
    eventBus.on(event.refresh, this.refreshData, this);
  },
  beforeDestroy() {
    eventBus.off(event.refresh, this.refreshData, this);
  },
  methods: {
    onChangeSelectedInformationHandler(value) {
      this.$emit('on-change-selected-information', value);
    },
    showAllHandler(value) {
      this.showAll = !value;
    },
    refreshData({ wait }) {
      wait(this.getGroupList());
    },
    enableLoading() {
      this.loading = true && !this.supressLoading;
    },
    disableLoading() {
      this.loading = false;
      this.supressLoading = false;
      this.requestingData = false;
    },
    getGroupList() {
      this.enableLoading();
      this.requestingData = true;
      return GeneralTabService
        .getGeneralTabList(this.selectedAssetModuleId)
        .then((data) => {
          this.groupList = data;
        })
        .finally(() => {
          this.disableLoading();
        });
    },
    quitRenaming() {
      this.groupList = this.groupList.filter(({ id }) => id);
    },
    createGroup() {
      if (!this.groupList.length) {
        return;
      }
      const { type } = this.groupList[0];
      this.groupList = this.groupList.concat({
        type,
        name: null,
        id: null,
        variables: [],
      });
    },
    apiCreateGroup(name, type) {
      const payload = {
        type,
        name,
        assetModuleId: this.selectedAssetModuleId,
      };
      const lastGroupIndex = this.groupList.length - 1;
      GeneralTabService
        .createNewSupervisionGroup(payload)
        .then(({ id }) => {
          this.groupList[lastGroupIndex].id = id;
          this.$pushAlert({ code: 'MG006', mode: alertModes.TOAST });
        })
        .catch(() => {
          this.quitRenaming();
        });
    },
    renameGroupRequest({ newName, id, type }) {
      if (!id) {
        return this.apiCreateGroup(newName, type);
      }
      return GeneralTabService
        .renameGroup({ newName }, id)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        })
        .catch(() => {
          this.getGroupList();
        });
    },
    deleteGroupRequest(id) {
      GeneralTabService
        .deleteGroup(id)
        .then(() => {
          this.getGroupList();
          this.$pushAlert({ code: 'MG007', mode: alertModes.TOAST });
        });
    },
    renameVariableRequest({ name, id }) {
      const groupIndex = this.groupList.findIndex(({ variables }) => {
        const ids = variables.map(item => item.id);
        return ids.includes(id);
      });
      const variableIndex = this.groupList[groupIndex].variables.findIndex(item => item.id === id);
      GeneralTabService
        .renameVariable({ name }, id)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
          this.groupList[groupIndex].variables[variableIndex].name = name;
          this.groupList[groupIndex].variables[variableIndex].hasOriginalName = false;
        });
    },
    applyFilter(filter) {
      this.filter = filter.map(({ id }) => id);
    },
    resetVariable(id) {
      this.requestingData = true;
      GeneralTabService
        .resetVariableName(id)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        })
        .finally(() => {
          this.supressLoading = true;
          this.getGroupList();
        });
    },
    toggleVisibility({ id, visibility }) {
      this.requestingData = true;
      GeneralTabService
        .toggleVariableVisibility(id, visibility)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        })
        .finally(() => {
          this.supressLoading = true;
          this.getGroupList();
        });
    },
    changeVariableGroup(payload) {
      const {
        destinyGroupId,
        originGroupId,
        variableId,
        groupSupervisionFieldId,
        order,
      } = payload;
      this.requestingData = true;
      GeneralTabService
        .moveVariable({
          destinyGroupId,
          originGroupId,
          groupSupervisionFieldId,
          order,
        }, variableId)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        })
        .finally(() => {
          this.supressLoading = true;
          this.getGroupList();
        });
    },
    restoreGroups(payload) {
      this.groupList = [];
      this.requestingData = true;
      this.enableLoading();
      GeneralTabService.restoreGroups(payload)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        }).finally(() => {
          this.supressLoading = true;
          this.getGroupList();
        });
    },
    searchChange(search) {
      this.variables = this.variableList
        .filter(({ name }) => name.toLocaleLowerCase().startsWith(search.toLocaleLowerCase()));
    },
  },
};
</script>
